/**
 * PlanGate — overlay global affiché quand la commune n'a plus de plan actif.
 *
 * Écoute l'événement `vpc:plan-inactif` émis par le client API lorsqu'une réponse
 * 403 PLAN_INACTIF (staff) ou PLAN_INACTIF_LECTURE_SEULE (administré) survient.
 *
 * - Staff : overlay bloquant plein écran (l'app n'est pas utilisable).
 * - Administré : bandeau d'information non bloquant (lecture seule).
 */

import { useEffect, useState } from 'react';
import { useAuth } from '@/lib/auth-context';
import { AlertTriangle, X } from 'lucide-react';

interface PlanEventDetail {
  error: string;
  code?: string;
  essaiExpire?: boolean;
}

export function PlanGate() {
  const { user } = useAuth();
  const [message, setMessage] = useState<string | null>(null);
  const [lectureSeule, setLectureSeule] = useState(false);

  useEffect(() => {
    function onPlan(e: Event) {
      const detail = (e as CustomEvent<PlanEventDetail>).detail;
      if (!detail) return;
      setMessage(detail.error);
      setLectureSeule(detail.code === 'PLAN_INACTIF_LECTURE_SEULE');
    }
    window.addEventListener('vpc:plan-inactif', onPlan);
    return () => window.removeEventListener('vpc:plan-inactif', onPlan);
  }, []);

  if (!message) return null;

  // Administré : bandeau d'information non bloquant (lecture seule autorisée).
  if (lectureSeule || user?.role === 'administre') {
    return (
      <div className="fixed inset-x-0 top-0 z-[120] flex items-start justify-center px-3 pt-3" role="status">
        <div className="flex w-full max-w-2xl items-start gap-3 rounded-r-md border border-amber-400/40 bg-amber-50/95 px-4 py-3 text-sm text-amber-900 shadow-soft backdrop-blur dark:bg-amber-950/90 dark:text-amber-100">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div className="flex-1">{message}</div>
          <button type="button" onClick={() => setMessage(null)} aria-label="Fermer" className="shrink-0 opacity-70 hover:opacity-100">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  // Staff : overlay bloquant plein écran.
  return (
    <div className="fixed inset-0 z-[130] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" role="alertdialog" aria-modal="true" aria-labelledby="plangate-titre" aria-describedby="plangate-message">
      <div className="w-full max-w-md rounded-r-lg border border-border bg-surface p-6 text-center shadow-xl">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-urgent/15">
          <AlertTriangle className="h-6 w-6 text-urgent" />
        </div>
        <h2 id="plangate-titre" className="font-display text-lg font-semibold text-ink">Accès suspendu</h2>
        <p id="plangate-message" className="mt-2 text-sm text-ink-muted">{message}</p>
        <p className="mt-4 text-xs text-ink-subtle">
          Pour réactiver l'accès, contactez One For All Distribution :<br />
          contact@oneforall-distribution.fr · 06 03 21 15 51
        </p>
      </div>
    </div>
  );
}
